/**
 * configs/runtime-config — where this widget learns which install it belongs to
 * (spec-onprem-readiness §2J, deploy gate 10.3-1; the §B "both messengers default to
 * production SaaS" finding).
 *
 * THE BUG THIS CLOSES
 * -------------------
 * `http-config.js` and `utils/common.js` each derived the API host, the socket host and
 * the voice-widget host from `window.location.hostname`, with a final `else` that
 * returned the Intelswift PRODUCTION SaaS endpoints. The widget is the one bundle we
 * ship onto hosts we do not own, so that `else` was reached by every customer-hosted
 * install: an on-prem widget quietly opened a socket to the Intelswift cloud socket host and
 * posted visitor messages to `app.intelswift.com`.
 *
 * THE MECHANISM
 * -------------
 * `index.html` loads `/env.js` — a no-op placeholder in the repo, rewritten at container
 * start by `docker-entrypoint.sh` from `env.template.js` (envsubst) — before the app
 * module. It assigns `window.__ENV`. Read it through this module only.
 *
 * RESOLUTION, per key (this bundle reads no build-time env at all, so there is no middle
 * layer here — unlike web-application-ui, which keeps its REACT_APP_* fallbacks):
 *   1. `window.__ENV.<KEY>` — runtime, written by the entrypoint.            ← on-prem
 *   2. the host default     — the Intelswift values for the Intelswift widget hosts
 *                             (`widget.` / `stage-widget.` / localhost), which keeps cloud
 *                             byte-identical; and for ANY OTHER host this widget's own
 *                             origin, never Intelswift's. That last clause is the fix: an
 *                             unconfigured on-prem widget now fails visibly against its own
 *                             origin instead of succeeding against our cloud.
 *
 * @typedef {"production" | "stage" | "development" | "unknown"} AppEnv
 * @typedef {Object} RuntimeConfig
 * @property {AppEnv}  appEnv            which install this is
 * @property {string}  apiOrigin         origin serving /api/v1, /channel-service/, /chat-service/, …
 * @property {string}  socketUrl         ws(s) origin of the chat socket service
 * @property {string}  voiceWidgetOrigin origin serving the voice widget iframe
 */

/** @returns {Record<string, string|undefined>} */
function bag() {
	return (typeof window !== "undefined" && window.__ENV) || {};
}

/** envsubst leaves `${FOO}` behind when FOO is unset — treat it, and "", as unconfigured. */
function isSet(value) {
	return typeof value === "string" && value !== "" && !(value.startsWith("${") && value.endsWith("}"));
}

function read(key) {
	const runtime = bag()[key];
	return isSet(runtime) ? runtime : undefined;
}

function hostname() {
	return (typeof window !== "undefined" && window.location && window.location.hostname) || "";
}

/**
 * Which install this is. The stage-before-localhost order is the original one from
 * `getDomain()`; the difference is the last line — an unrecognised host is "unknown"
 * (it used to be "production", i.e. Intelswift's cloud).
 * @returns {AppEnv}
 */
function detectAppEnv() {
	const host = hostname();
	if (host.includes("stage")) return "stage";
	if (host.includes("localhost") || host.includes("127.0.0.1")) return "development";
	if (host.endsWith("intelswift.com")) return "production";
	return "unknown";
}

const SAAS = {
	apiOrigin: {
		production: "https://app.intelswift.com",
		stage: "https://stage-app.intelswift.com",
		development: "http://localhost",
	},
	// D11 host fold (socket foundation, 2026-09-06): socket-service answers on ONE path,
	// `/socket-service/api/v1`, on the SAME origin as the API — the `chat.intelswift.com` /
	// `stage-chat.intelswift.com` hosts that used to front it are retired. `socketUrl()` in
	// http-config appends the path, so these stay origins and a configured SOCKET_URL that
	// already carries it is not doubled.
	socketUrl: {
		production: "wss://app.intelswift.com",
		stage: "wss://stage-app.intelswift.com",
		development: "ws://localhost:8000",
	},
	voiceWidgetOrigin: {
		production: "https://voice.intelswift.com",
		stage: "https://stage-voice.intelswift.com",
		development: "http://localhost:3003",
	},
};

/** This widget's own origin — the safe answer when nothing is configured. */
function ownOrigin() {
	if (typeof window === "undefined" || !window.location) return "";
	return window.location.origin || "";
}

/** ws(s):// on this widget's own host. */
function ownSocketOrigin() {
	if (typeof window === "undefined" || !window.location) return "";
	const secure = window.location.protocol === "https:";
	return `${secure ? "wss" : "ws"}://${window.location.host}`;
}

let warned = false;
function warnUnconfigured() {
	if (warned || typeof console === "undefined") return;
	warned = true;
	console.error(
		"[intelswift] no runtime config: window.__ENV is missing API_ORIGIN/SOCKET_URL on an " +
		"unrecognised host. Falling back to this widget's own origin. The container entrypoint " +
		"(docker-entrypoint.sh → env.js) has not run, or its variables are unset."
	);
}

/** @returns {RuntimeConfig} */
export function getRuntimeConfig() {
	const configuredEnv = read("APP_ENV");
	const appEnv =
		configuredEnv === "production" || configuredEnv === "stage" || configuredEnv === "development"
			? configuredEnv
			: detectAppEnv();

	const apiOrigin = read("API_ORIGIN");
	const socketUrl = read("SOCKET_URL");
	const voiceWidgetOrigin = read("VOICE_WIDGET_ORIGIN");

	if (appEnv === "unknown" && !(apiOrigin && socketUrl)) warnUnconfigured();

	return {
		appEnv,
		apiOrigin: apiOrigin || SAAS.apiOrigin[appEnv] || ownOrigin(),
		socketUrl: socketUrl || SAAS.socketUrl[appEnv] || ownSocketOrigin(),
		voiceWidgetOrigin: voiceWidgetOrigin || SAAS.voiceWidgetOrigin[appEnv] || ownOrigin(),
	};
}
